/*
Risk Register Service

Wraps /api/v1/rsk/risks. Schema mirrors the simple maker-checker pattern of
controlRegisterService — `is_active` (1/0/-1) + `is_approved` (1/0/-1) on the
backend; the UI derives a string `status` from those.
*/

import apiClient from './apiService';
import { AxiosResponse } from 'axios';

const apiService = apiClient;

// =============================================================================
// Types
// =============================================================================

export interface RiskRegister {
  id: number;
  entity_id: number;
  risk_code: string;
  risk_name: string;
  description?: string;
  category_id?: number;
  category_name?: string;
  is_active: number;
  is_approved: number;
  created_by?: number;
  created_on?: string;
  modified_by?: number;
  modified_on?: string;
  // Workflow quad (maker-checker) — drives the permission engine
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  submitted_by?: number;
  submitted_on?: string;
  approved_by?: number;
  approved_on?: string;
  approval_note?: string;
  rejected_by?: number;
  rejected_on?: string;
  rejection_reason?: string;
  // Downstream link counts (aggregated in the list query, no N+1).
  // control_count = controls mitigating this risk (direct); sop_count = SOPs reached indirectly via those controls.
  control_count?: number | null;
  sop_count?: number | null;
  // UI-only derived field
  status?: string;
  // System-generated code (e.g. "RSK-1") — null for drafts, set on submit
  code?: string | null;
}

export interface RiskCategory {
  id: number;
  category_name: string;
  description?: string;
}

export interface RiskQueryParams {
  status?: 'ALL' | 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED' | 'ACTIVE' | 'INACTIVE';
  search?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  total?: number;
  meta?: { total: number; page: number; limit: number; pages: number };
}

// Derive a status string — prefers the real workflow_state, falls back to the
// legacy is_approved flag for any not-yet-migrated rows.
export function deriveRiskStatus(r: RiskRegister): string {
  const ws = r.workflow_state;
  if (ws) {
    if (ws === 'approved') return r.is_active === 1 ? 'APPROVED' : 'INACTIVE';
    if (ws === 'submitted') return 'PENDING_APPROVAL';
    if (ws === 'rejected') return 'REJECTED';
    return 'DRAFT';
  }
  if (r.is_approved === 1) return r.is_active === 1 ? 'APPROVED' : 'INACTIVE';
  if (r.is_approved === -1) return 'REJECTED';
  return 'DRAFT';
}

// =============================================================================
// Service
// =============================================================================

class RiskRegisterService {
  async getAll(params?: RiskQueryParams): Promise<RiskRegister[]> {
    const queryParams = new URLSearchParams();
    if (params?.status && params.status !== 'ALL') {
      queryParams.append('status', params.status);
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    // The component paginates client-side (risks.slice), so pull the full set —
    // the backend default limit is 50, which silently hid risks beyond the 50th.
    queryParams.append('limit', '200');
    const queryString = queryParams.toString();
    const url = `/api/v1/rsk/risks${queryString ? `?${queryString}` : ''}`;

    try {
      const response: AxiosResponse<ApiResponse<RiskRegister[]>> = await apiService.get(url);
      const items = response.data?.data ?? [];
      return items.map(r => ({ ...r, status: deriveRiskStatus(r) }));
    } catch (e: any) {
      if (e.response?.status === 401) window.location.href = '/login';
      throw e;
    }
  }

  async getById(id: number): Promise<RiskRegister> {
    const response: AxiosResponse<ApiResponse<RiskRegister>> = await apiService.get(`/api/v1/rsk/risks/${id}`);
    const r = response.data?.data;
    if (!r) throw new Error(response.data?.error || 'Failed to fetch risk');
    return { ...r, status: deriveRiskStatus(r) };
  }

  async create(data: Partial<RiskRegister>, action: 'draft' | 'submit' | 'approve' = 'draft'): Promise<RiskRegister> {
    const response: AxiosResponse<ApiResponse<RiskRegister>> = await apiService.post(
      `/api/v1/rsk/risks?action=${action}`, data,
    );
    const r = response.data?.data;
    if (!r) throw new Error(response.data?.error || 'Failed to create risk');
    return { ...r, status: deriveRiskStatus(r) };
  }

  async update(id: number, data: Partial<RiskRegister>): Promise<RiskRegister> {
    const response: AxiosResponse<ApiResponse<RiskRegister>> = await apiService.put(`/api/v1/rsk/risks/${id}`, data);
    const r = response.data?.data;
    if (!r) throw new Error(response.data?.error || 'Failed to update risk');
    return { ...r, status: deriveRiskStatus(r) };
  }

  async delete(id: number): Promise<void> {
    await apiService.delete(`/api/v1/rsk/risks/${id}`);
  }

  async toggleStatus(id: number): Promise<RiskRegister> {
    const response: AxiosResponse<ApiResponse<RiskRegister>> = await apiService.patch(`/api/v1/rsk/risks/${id}/toggle`);
    const r = response.data?.data;
    if (!r) throw new Error(response.data?.error || 'Failed to toggle');
    return { ...r, status: deriveRiskStatus(r) };
  }

  async submit(id: number): Promise<RiskRegister> {
    const response: AxiosResponse<ApiResponse<RiskRegister>> = await apiService.patch(`/api/v1/rsk/risks/${id}/submit`);
    const r = response.data?.data;
    if (!r) throw new Error(response.data?.error || 'Failed to submit');
    return { ...r, status: deriveRiskStatus(r) };
  }

  async approve(id: number, note?: string): Promise<RiskRegister> {
    const response: AxiosResponse<ApiResponse<RiskRegister>> = await apiService.patch(`/api/v1/rsk/risks/${id}/approve`, { note });
    const r = response.data?.data;
    if (!r) throw new Error(response.data?.error || 'Failed to approve');
    return { ...r, status: deriveRiskStatus(r) };
  }

  async reject(id: number, reason?: string): Promise<RiskRegister> {
    const response: AxiosResponse<ApiResponse<RiskRegister>> = await apiService.patch(`/api/v1/rsk/risks/${id}/reject`, { reason });
    const r = response.data?.data;
    if (!r) throw new Error(response.data?.error || 'Failed to reject');
    return { ...r, status: deriveRiskStatus(r) };
  }

  async deactivate(id: number): Promise<RiskRegister> {
    const response: AxiosResponse<ApiResponse<RiskRegister>> = await apiService.patch(`/api/v1/rsk/risks/${id}/deactivate`);
    const r = response.data?.data;
    if (!r) throw new Error(response.data?.error || 'Failed to deactivate');
    return { ...r, status: deriveRiskStatus(r) };
  }

  // Approved + active risks for pickers (RAT scope, etc). Unified /dropdown
  // endpoint — never leaks drafts/unapproved risks.
  async getDropdown(): Promise<{ id: number; name: string }[]> {
    const response = await apiService.get('/api/v1/rsk/risks/dropdown');
    return response.data?.items ?? [];
  }

  async getActiveCategories(): Promise<RiskCategory[]> {
    // Use the unified dropdown endpoint so only ACTIVE + APPROVED categories
    // (green-dot) appear — /categories?status=ACTIVE filters active only and
    // would leak draft/submitted categories into the picker.
    const response = await apiService.get('/api/v1/rsk/categories/dropdown');
    const items = response.data?.items ?? [];
    return items.map((it: { id: number; name: string }) => ({
      id: it.id,
      category_name: it.name,
    }));
  }

  // ---- UI helpers ----

  validateData(data: Partial<RiskRegister>, isUpdate = false): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    if (!isUpdate) {
      if (!data.risk_code || data.risk_code.trim() === '') errors.push('Risk code is required');
      if (!data.risk_name || data.risk_name.trim() === '') errors.push('Risk name is required');
    }
    if (data.risk_code && !/^[A-Z0-9-]+$/.test(data.risk_code)) {
      errors.push('Risk code must contain only uppercase letters, numbers, and hyphens');
    }
    return { valid: errors.length === 0, errors };
  }

  getStatusBadgeClass(status: string): string {
    const m: Record<string, string> = {
      DRAFT: 'badge-secondary',
      PENDING_APPROVAL: 'badge-warning',
      APPROVED: 'badge-success',
      REJECTED: 'badge-danger',
      INACTIVE: 'badge-secondary',
    };
    return m[status] || 'badge-secondary';
  }

  getStatusDisplayText(status: string): string {
    const m: Record<string, string> = {
      DRAFT: 'Draft',
      PENDING_APPROVAL: 'Pending Approval',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      INACTIVE: 'Inactive',
    };
    return m[status] || status;
  }

  canEdit(r: RiskRegister): boolean { return r.status === 'DRAFT' || r.status === 'REJECTED'; }
  canSubmit(r: RiskRegister): boolean { return r.status === 'DRAFT' || r.status === 'REJECTED'; }
  canDelete(r: RiskRegister): boolean { return r.status === 'DRAFT' || r.status === 'REJECTED'; }
  canToggle(r: RiskRegister): boolean { return r.status === 'APPROVED' || r.status === 'INACTIVE'; }
}

const riskRegisterService = new RiskRegisterService();
export default riskRegisterService;
